'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Images, Check } from 'lucide-react';
import { AdaptiveDialog } from '@/shared/ui/AdaptiveDialog';
import { Typography } from '@/shared/ui/shadcn/typography';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { conceptsService } from '@/shared/api/services/concepts.service';
import { useLimitErrorPaywall } from '@/shared/hooks/useLimitErrorPaywall';
import { useSubscriptionLimits } from '@/features/subscription';
import { useCreateConcept } from '@/features/design/concept/hooks/use-concepts';
import {
  compareByViewSuitability,
  isProjectAngleValid,
  isRenderCandidate,
  type ProjectAngle,
} from '@/types/project-angle';
import { AngleSelectionRow } from '@/features/angles-overview/ui/AngleSelectionRow';
import type { ConceptAngle } from '@/types/concept';

// Discriminated union: the modal serves two flows that share the same UI —
// creating a brand-new concept (no conceptId yet), and editing an existing
// concept's angle set. Only the mutation target and the wording differ.
type ConceptAnglesEditModalProps = {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & (
  | {
      mode: 'edit';
      conceptId: string;
      conceptAngles: ConceptAngle[];
      // Receives angle IDs newly attached relative to the baseline when the
      // modal opened, so the parent can surface the new derivative.
      onComplete: (newlyAttachedAngleIds: string[]) => void;
    }
  | {
      mode: 'create';
      // Called after the new concept is created server-side. Parent is
      // expected to navigate to the concept page.
      onCreated: (conceptId: string) => void;
      /**
       * Angle ids to pre-select on open. Used by the post-upload finalize
       * handoff: the project page uploads images as project-angles, then opens
       * this modal with the freshly uploaded ids prefilled so the user picks
       * main + reorder without manually searching the library.
       */
      prefilledAngleIds?: string[];
      /**
       * Secondary action for the create sheet: instead of a bare "Cancel", the
       * sheet offers "Add files" — it closes and hands the user straight to the
       * file picker. Surfaces that host the Brief intake pass their own opener;
       * without one the modal falls back to the Brief page, which owns the
       * intake (same destination as the empty-state link).
       */
      onAddFiles?: () => void;
    }
);

export function ConceptAnglesEditModal(props: ConceptAnglesEditModalProps) {
  const { projectId, open, onOpenChange } = props;
  const mode = props.mode;
  // Pre-extract mode-specific handlers so the submit callback doesn't have to
  // re-narrow the discriminated union on every call. Branching on `props.mode`
  // keeps TypeScript's discriminated-union narrowing intact.
  const editConceptId = props.mode === 'edit' ? props.conceptId : undefined;
  const editOnComplete = props.mode === 'edit' ? props.onComplete : undefined;
  const createOnCreated = props.mode === 'create' ? props.onCreated : undefined;
  const createOnAddFiles =
    props.mode === 'create' ? props.onAddFiles : undefined;
  const t = useTranslations('angles');
  const tCommon = useTranslations('common');
  const tIntake = useTranslations('briefIntake');

  const { data: angles = [] } = useProjectAngles(projectId);

  // Per-concept angle cap from the effective plan (real subscription OR guest
  // fallback computed server-side). Guards the toggle so users can't select
  // past the plan limit between open and Save.
  const limits = useSubscriptionLimits();
  const maxAnglesAllowed = Math.max(1, (limits?.angles as number) ?? 2);

  // Baseline: in edit mode it's the server-truth angle_ids when the modal was
  // opened; in create mode there's nothing to compare against, so an empty set.
  const conceptAngles =
    props.mode === 'edit' ? props.conceptAngles : undefined;
  const baselineAngleIds = useMemo(() => {
    if (!conceptAngles) return [] as string[];
    return conceptAngles
      .map((ca) => ca.relationships?.angle?.data?.id)
      .filter((id): id is string => !!id);
  }, [conceptAngles]);

  // create mode only: angle ids the caller wants prefilled on open
  // (post-upload finalize handoff). Falls back to empty.
  const prefilledAngleIds = useMemo(
    () => (props.mode === 'create' ? props.prefilledAngleIds ?? [] : []),
    [props.mode === 'create' ? props.prefilledAngleIds : undefined]
  );

  // Draft: ordered project-angle IDs. First element is main, rest are derivatives.
  // All toggle / reorder interactions mutate this list only — no network traffic
  // until the user hits Save/Create. Cancel just closes the modal and the draft dies.
  const [draftAngleIds, setDraftAngleIds] =
    useState<string[]>(baselineAngleIds);
  const [isSaving, setIsSaving] = useState(false);

  // Info-source angles (drawings/documents) are soft-deleted server-side so
  // they never reach this list — the isRenderCandidate guard is belt-and-
  // suspenders against any that still slip through (Brief-intake Phase 3).
  // Sorted best-source-first (photo > render > sketch > flat projection),
  // tie-broken by manual order, so the auto-seeded main and the suggestion list
  // surface the strongest view to redesign. selectedAngles keeps the user's own
  // draft order (it maps over draftAngleIds), so this sort only affects the
  // seed and the unselected suggestions. Mirrors Apple/Android.
  const validAngles = useMemo(
    () =>
      angles
        .filter((a) => isProjectAngleValid(a) && isRenderCandidate(a))
        .sort(compareByViewSuitability),
    [angles]
  );

  // Re-seed draft when the modal (re)opens with fresh server data. Without this,
  // a stale draft from a previous open could leak in on reopen. In create mode
  // the post-upload handoff seeds the draft with the freshly-uploaded ids
  // (prefilledAngleIds) so the user lands on the sheet with their just-taken
  // photos already selected and ordered (first = main).
  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      // Filter prefill against validAngles so ids that haven't completed
      // server-side validation yet (or that came back invalid) don't show as
      // "selected" — the row wouldn't render and the count would lie.
      // Mirrors Apple ConceptAnglesView and Android ConceptAnglesViewModel.
      const validIds = new Set(validAngles.map((a) => a.id));
      const filtered = prefilledAngleIds.filter((id) => validIds.has(id));
      // Create mode always opens with at least one view selected (and the
      // main can't be deselected — see handleToggle). When nothing is
      // prefilled, seed ALL valid library views (best-source-first, so index 0
      // is main) up to the per-concept cap, so a building with several photos
      // lands on a ready-to-create sheet with every view pre-selected. Mirrors
      // Apple ConceptAnglesView.autoSelectAngles and Android
      // ConceptAnglesViewModel.autoSelectAngles (both seed the full valid set
      // capped at the plan limit, not just the single main view).
      const seed =
        filtered.length > 0 ? filtered : validAngles.map((a) => a.id);
      // Cap to the per-concept limit; the picker already enforces this for
      // toggles but the prefill comes from a server-side upload where the
      // library cap is the only constraint.
      setDraftAngleIds(seed.slice(0, maxAnglesAllowed));
    } else {
      setDraftAngleIds(baselineAngleIds);
    }
  }, [open, baselineAngleIds, mode, prefilledAngleIds, maxAnglesAllowed, validAngles]);

  const selectedAngles = useMemo(
    () =>
      draftAngleIds
        .map((id) => validAngles.find((a) => a.id === id))
        .filter((a): a is ProjectAngle => !!a),
    [draftAngleIds, validAngles]
  );

  const unselectedAngles = useMemo(
    () => validAngles.filter((a) => !draftAngleIds.includes(a.id)),
    [validAngles, draftAngleIds]
  );

  const hasChanges = useMemo(
    () =>
      draftAngleIds.length !== baselineAngleIds.length ||
      draftAngleIds.some((id, i) => id !== baselineAngleIds[i]),
    [draftAngleIds, baselineAngleIds]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftAngleIds((current) => {
      const oldIndex = current.indexOf(String(active.id));
      const newIndex = current.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return current;
      return arrayMove(current, oldIndex, newIndex);
    });
  }, []);

  const handleToggle = useCallback(
    (projectAngleId: string) => {
      setDraftAngleIds((current) => {
        const index = current.indexOf(projectAngleId);
        if (index === -1) {
          // At the per-concept limit — ignore further additions. The UI also
          // dims unselected rows to make this state visible.
          if (current.length >= maxAnglesAllowed) return current;
          return [...current, projectAngleId];
        }
        if (index === 0) {
          // Main is the first item — it can never be deselected in either
          // mode, so at least one view always stays selected. The user
          // promotes a new main by reordering (drag the new main into slot 0).
          // Mirrors Apple ConceptAnglesView (toggleInCreate/toggleInEdit).
          return current;
        }
        return current.filter((id) => id !== projectAngleId);
      });
    },
    [maxAnglesAllowed]
  );

  const isAtLimit = draftAngleIds.length >= maxAnglesAllowed;

  const routeLimitError = useLimitErrorPaywall();
  const createConceptMutation = useCreateConcept();

  const handleSubmit = useCallback(async () => {
    if (isSaving || draftAngleIds.length === 0) return;
    if (mode === 'edit' && !hasChanges) return;
    setIsSaving(true);
    try {
      if (mode === 'edit' && editConceptId && editOnComplete) {
        const items = draftAngleIds.map((angleId, index) => ({
          angle_id: angleId,
          type: index === 0 ? ('main' as const) : ('derivative' as const),
        }));
        await conceptsService.syncConceptAngles(editConceptId, items);
        onOpenChange(false);
        const baseline = new Set(baselineAngleIds);
        const newlyAttached = draftAngleIds.filter((id) => !baseline.has(id));
        editOnComplete(newlyAttached);
      } else if (mode === 'create' && createOnCreated) {
        const [mainAngleId, ...derivativeAngleIds] = draftAngleIds;
        if (!mainAngleId) return;
        const newConcept = await createConceptMutation.mutateAsync({
          projectId,
          mainAngleId,
          derivativeAngleIds,
        });
        onOpenChange(false);
        createOnCreated(newConcept.id);
      }
    } catch (err) {
      if (!routeLimitError(err)) {
        console.error('Failed to submit concept angles:', err);
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    hasChanges,
    draftAngleIds,
    mode,
    projectId,
    baselineAngleIds,
    onOpenChange,
    createConceptMutation,
    routeLimitError,
    editConceptId,
    editOnComplete,
    createOnCreated,
  ]);

  const handleCancel = useCallback(() => {
    // Discard the draft; server state is untouched.
    onOpenChange(false);
  }, [onOpenChange]);

  const router = useRouter();
  // Empty-state ("No views") link: close the modal and open the Brief page,
  // which now hosts the building's photos, so the user can add views before
  // creating a concept — parity with Apple/Android, which point to the photo
  // library rather than a disk upload here.
  const handleGoToViews = useCallback(() => {
    onOpenChange(false);
    router.push(`/project/${projectId}/goals`);
  }, [onOpenChange, router, projectId]);

  // Create mode replaces "Cancel" with "Add files" — right after an upload the
  // useful next step is adding more views, not dismissing (parity with
  // Apple/Android). Falls back to the Brief page when the host has no intake.
  const handleAddFiles = useCallback(() => {
    onOpenChange(false);
    if (createOnAddFiles) {
      createOnAddFiles();
      return;
    }
    router.push(`/project/${projectId}/goals`);
  }, [onOpenChange, createOnAddFiles, router, projectId]);

  const titleKey =
    mode === 'create' ? 'conceptAngles.createTitle' : 'conceptAngles.title';
  const submitLabel =
    mode === 'create'
      ? t('conceptAngles.createConcept')
      : tCommon('save');
  const submitDisabled =
    isSaving ||
    draftAngleIds.length === 0 ||
    (mode === 'edit' && !hasChanges);

  return (
    <AdaptiveDialog
      open={open}
      onClose={handleCancel}
      title={t(titleKey)}
      onSave={handleSubmit}
      saveEnabled={!submitDisabled}
      isSaving={isSaving}
      saveLabel={submitLabel}
      cancelLabel={mode === 'create' ? tIntake('addFiles') : undefined}
      onCancelAction={mode === 'create' ? handleAddFiles : undefined}
      // Both modes confirm with a checkmark, not a word — the sheet only
      // assembles views; the render comes later (parity with Apple iOS 26 HIG /
      // Android, which use a checkmark for the edit "Save" too). saveLabel stays
      // as the accessible aria-label.
      saveIcon={<Check className="w-5 h-5" />}
      saveAccent
      groupedSurface
    >
      {validAngles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Images className="w-16 h-16 text-muted-foreground mb-4" />
          <Typography variant="h5" className="mb-2">
            {t('conceptAngles.noValidAngles')}
          </Typography>
          <Typography variant="muted" className="max-w-sm">
            {t.rich('conceptAngles.noValidAnglesDescription', {
              link: (chunks) => (
                <button
                  type="button"
                  onClick={handleGoToViews}
                  className="text-primary underline underline-offset-2 hover:opacity-80"
                >
                  {chunks}
                </button>
              ),
            })}
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-4 pt-2">
          {/* Selected views — one white grouped card on the muted sheet
              (mirrors Apple's grouped Form section). */}
          {selectedAngles.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={draftAngleIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="rounded-2xl bg-card p-1 space-y-0.5">
                  {selectedAngles.map((angle, idx) => (
                    <AngleSelectionRow
                      key={angle.id}
                      angle={angle}
                      index={validAngles.indexOf(angle)}
                      isSelected={true}
                      selectionIndex={idx}
                      positionLabel={
                        // Create mode keeps only the "Main view" label (idx 0);
                        // other rows stay text-free. Edit mode labels every slot.
                        mode === 'create'
                          ? idx === 0
                            ? t('conceptAngles.position.1')
                            : undefined
                          : idx < 7
                            ? t(`conceptAngles.position.${idx + 1}`)
                            : undefined
                      }
                      compact={mode === 'create'}
                      onToggle={() => handleToggle(angle.id)}
                      sortable={true}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Remaining library views — second white grouped card. The section
              header is intentionally omitted to match Apple. */}
          {unselectedAngles.length > 0 && (
            <div className="rounded-2xl bg-card p-1 space-y-0.5">
              {unselectedAngles.map((angle) => (
                <AngleSelectionRow
                  key={angle.id}
                  angle={angle}
                  index={validAngles.indexOf(angle)}
                  isSelected={false}
                  selectionIndex={null}
                  disabled={isAtLimit}
                  compact={mode === 'create'}
                  onToggle={() => handleToggle(angle.id)}
                />
              ))}
            </div>
          )}

          {/* Hint footer — sits directly on the muted sheet, no card backing
              (mirrors Apple's clear list-row hint). Create mode shows a friendly
              description (no counter) since views can be added at any time later;
              edit mode keeps the composition hint + the "selected / max" capsule,
              which is meaningful when curating an existing concept's view set. */}
          {mode === 'create' ? (
            <Typography variant="muted" className="text-form-help !mt-0 px-2">
              {t('conceptAngles.createHint')}
            </Typography>
          ) : (
            <div className="flex items-center justify-between gap-3 px-2">
              <Typography variant="muted" className="text-form-help !mt-0">
                {t('conceptAngles.selectHint')}
              </Typography>
              <span className="inline-flex items-center rounded-full bg-primary/15 px-3 py-0.5 text-list-item-value text-primary">
                {draftAngleIds.length}/{maxAnglesAllowed}
              </span>
            </div>
          )}
        </div>
      )}
    </AdaptiveDialog>
  );
}
